import React, { useCallback, useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import L, { LatLngTuple } from 'leaflet'
import { CSVLink } from 'react-csv'
import CSVReader from 'react-csv-reader'
import { colors } from '../../../../../constants'
import { FlexContainer, YSpacing, DividerLine } from '@components/common'
import { Button, MultiSelectDropDown } from '@components/common/form'
import {
  getMenuCoordinates,
  calcMenuTemplateOptions,
  generateMenuTemplateSuggestions,
  dateMenuSuggestionToCSV,
  csvToDateMenuSuggestions,
} from '../menuSuggestionsHelpers'
import {
  Coordinate,
  GOScheduleMenu,
  DateMenuTemplate,
  DateMenuSuggestion,
  ChefMenuGroup,
  ShowInfoModalReq,
  Option,
  Chef,
} from '@types'
import MenuDetails from '../MenuDetails'
import DateMenuTemplatesManager from '../DateMenuTemplates/DateMenuTemplatesManager'
import { FaCircleXmark, FaCaretDown, FaCaretUp } from 'react-icons/fa6'
import { FaFileDownload, FaFileUpload } from 'react-icons/fa'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
  SearchDateMenuTemplatesReq,
} from '@coordinators/groupOrders/dateMenuTemplates'

// Chef icon for map markers (unselected chefs)
const purpleChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=9775FA',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

// Chef icon for map markers (selected chefs)
const greenChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=4CAF50',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

const yellowChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=FFDE21',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

// Types

interface MapMarker {
  id: string
  position: LatLngTuple
  icon: L.Icon
  popupContent: JSX.Element
}

interface MapPolyline {
  positions: LatLngTuple[]
  pathOptions: {
    color: string
    weight: number
    opacity: number
  }
}

/**
 * Custom hook for managing market chefs map and creating suggestions
 * from multiple selected chefs as targets
 */
interface UseMarketChefsMapParams {
  hqId: number
  menus: GOScheduleMenu[]
  displayInfoMessage: (message: string) => void
  displayFailureMessage: (message: string) => void
}

interface UseMarketChefsMapResult {
  chefMenuGroups: ChefMenuGroup[]
  filteredChefMenuGroups: ChefMenuGroup[]
  mapMarkers: MapMarker[]
  mapPolylines: MapPolyline[]
  mapCenter: LatLngTuple | null
  maxMiles: number | null
  setMaxMiles: (miles: number | null) => void
  searchTerm: string
  setSearchTerm: (term: string) => void
  includesCuisines: string[]
  setIncludesCuisines: (cuisines: string[]) => void
  excludesCuisines: string[]
  setExcludesCuisines: (cuisines: string[]) => void
  selectedChefIds: string[]
  handleSelectChef: (chefId: string) => void
  selectedTemplate: DateMenuTemplate | null
  selectedSuggestionId: number | null
  selectedGroupedChefSuggestions: string | null
  selectedSuggestionMenuId: string | null
  setSelectedTemplate: (template: DateMenuTemplate) => void
  handleClearSelections: () => void
  handleGenerateSuggestions: () => void
  handleSelectGroupedChefSuggestions: (chefId: string | null) => void
  handleSelectDateMenuSuggestion: (suggestionId: number | null) => void
  handleSelectSuggestionMenu: (menuId: string | null) => void
  handleImportCSV: (data: any[], _fileInfo: any) => boolean
  hasGeneratedSuggestions: boolean
  generatedSuggestions: DateMenuSuggestion[]
  groupedChefsSuggestions: Record<string, DateMenuSuggestion[]> | null
  includeAnchorChefs: boolean | null
  setIncludeAnchorChefs: (includeAnchorChefs: boolean | null) => void
}

export const useMarketChefsMap = ({
  hqId,
  menus,
  displayInfoMessage,
  displayFailureMessage,
}: UseMarketChefsMapParams): UseMarketChefsMapResult => {
  // Suggestion generation configs
  const [selectedChefIds, setSelectedChefIds] = useState<string[]>([])
  const [selectedTemplate, setSelectedTemplate] =
    useState<DateMenuTemplate | null>(null)
  const [allChefsSearch, setAllChefsSearch] = useState<string>('')
  const [includesCuisines, setIncludesCuisines] = useState<string[]>([])
  const [excludesCuisines, setExcludesCuisines] = useState<string[]>([])
  const [includeAnchorChefs, setIncludeAnchorChefs] = useState<boolean | null>(
    null,
  )
  const [maxMiles, setMaxMiles] = useState<number | null>(null)

  // Suggestion generation state
  const [generatedSuggestions, setGeneratedSuggestions] = useState<
    DateMenuSuggestion[]
  >([])
  const [hasGeneratedSuggestions, setHasGeneratedSuggestions] =
    useState<boolean>(false)

  // Map interaction state
  const [selectedGroupedChefSuggestions, setSelectedGroupedChefSuggestions] =
    useState<string | null>(null)
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<
    number | null
  >(null)
  const [selectedSuggestionMenuId, setSelectedSuggestionMenuId] = useState<
    string | null
  >(null)

  // State for map interaction
  const [mapCenter, setMapCenter] = useState<LatLngTuple | null>(null)

  // Calculate chef menu groups
  const chefMenuGroups = useMemo(() => {
    const chefMap = new Map<string, ChefMenuGroup>()

    menus.forEach((menu) => {
      if (menu.chef && menu.chef.id) {
        const coordinates = getMenuCoordinates(menu, hqId)

        if (chefMap.has(menu.chef.id)) {
          chefMap.get(menu.chef.id)?.menus.push(menu)
        } else {
          chefMap.set(menu.chef.id, {
            id: menu.chef.id,
            name: menu.chef.name,
            chef: menu.chef,
            chefPriorityType: menu.chefPriorityType,
            coordinates,
            menus: [menu],
          })
        }
      }
    })

    return Array.from(chefMap.values())
  }, [menus, hqId])

  // Filter chef menu groups based on search term and cuisine filters
  const filteredChefMenuGroups = useMemo(() => {
    const searchTermLower = allChefsSearch.trim().toLowerCase()

    return (
      chefMenuGroups
        .map((chefGroup) => {
          // First filter the chef's menus
          const filteredMenus = chefGroup.menus.filter((menu) => {
            // Search term filter
            const matchesSearch =
              !searchTermLower ||
              chefGroup.name.toLowerCase().includes(searchTermLower) ||
              menu.name.toLowerCase().includes(searchTermLower)

            if (!matchesSearch) {
              return false
            }

            // Includes cuisine filter
            const menuCuisines = menu.cuisineTags || []
            const matchesIncludesCuisines =
              includesCuisines.length === 0 ||
              includesCuisines.some((c) => menuCuisines.includes(c))

            if (!matchesIncludesCuisines) {
              return false
            }

            // Excludes cuisine filter
            const matchesExcludesCuisines =
              excludesCuisines.length === 0 ||
              !excludesCuisines.some((c) => menuCuisines.includes(c))

            if (!matchesExcludesCuisines) {
              return false
            }

            // Anchor chef filter
            const isAnchorChef = chefGroup.chefPriorityType === 'Anchor'
            const matchesAnchorChef =
              includeAnchorChefs === null || isAnchorChef === includeAnchorChefs

            return matchesAnchorChef
          })

          // Create a new chef group with the filtered menus
          return {
            ...chefGroup,
            menus: filteredMenus,
          }
        })
        // Then filter out chefs who have no menus left after filtering
        .filter((chefGroup) => chefGroup.menus.length > 0)
        // Sort by name
        .sort((a, b) => a.name.localeCompare(b.name))
    )
  }, [
    chefMenuGroups,
    allChefsSearch,
    includesCuisines,
    excludesCuisines,
    includeAnchorChefs,
  ])

  // Handle chef selection/deselection
  const handleSelectChef = useCallback(
    (chefId: string) => {
      setSelectedChefIds((prev) => {
        if (prev.includes(chefId)) {
          return prev.filter((id) => id !== chefId)
        } else {
          const chef = chefMenuGroups.find((group) => group.id === chefId)
          if (chef) {
            setMapCenter([
              chef.coordinates.latitude,
              chef.coordinates.longitude,
            ])
          }

          return [...prev, chefId]
        }
      })
    },
    [chefMenuGroups, setMapCenter],
  )

  // Clear all selections
  const handleClearSelections = useCallback(() => {
    setSelectedChefIds([])
    setHasGeneratedSuggestions(false)
    setGeneratedSuggestions([])
    setSelectedSuggestionId(null)
    setSelectedSuggestionMenuId(null)
  }, [])

  // Generate suggestions using selected chefs as targets
  const handleGenerateSuggestions = useCallback(() => {
    // Validate selections
    if (selectedChefIds.length === 0) {
      displayFailureMessage('Please select at least one chef')

      return
    }

    if (!selectedTemplate) {
      displayFailureMessage('Please select a date menu template')

      return
    }

    // Get target chefs for suggestions
    const targetChefs = chefMenuGroups.filter((group) =>
      selectedChefIds.includes(group.id),
    )

    // Update template to exclude target chefs
    const updatedTemplate = {
      ...selectedTemplate,
      templates: selectedTemplate.templates.map((template) => ({
        ...template,
        excludeChefIds: [
          ...(template.excludeChefIds || []),
          ...targetChefs.map((chef) => chef.id),
        ],
      })),
    }

    // Calculate menu template options
    const menuTemplateOptions = updatedTemplate.templates.map((template) =>
      calcMenuTemplateOptions(template, menus),
    )

    // Generate suggestions using the selected chefs as targets
    const { suggestions } = generateMenuTemplateSuggestions(
      menuTemplateOptions,
      targetChefs,
      hqId,
      maxMiles ? [{ maxDistance: maxMiles }] : undefined,
    )

    if (suggestions.length === 0) {
      displayFailureMessage(
        'No suggestions could be generated with the selected criteria',
      )

      return
    }

    // Update state with generated suggestions
    setGeneratedSuggestions(suggestions)
    setHasGeneratedSuggestions(true)
    setSelectedGroupedChefSuggestions(null)
    setSelectedSuggestionId(null)
    setSelectedSuggestionMenuId(null)
    displayInfoMessage(
      `Generated ${suggestions.length} suggestions from selected chefs`,
    )
  }, [
    selectedChefIds,
    selectedTemplate,
    chefMenuGroups,
    menus,
    hqId,
    displayInfoMessage,
    displayFailureMessage,
    maxMiles,
  ])

  const groupedChefsSuggestions = useMemo(() => {
    if (generatedSuggestions.length === 0) {
      return null
    }

    return generatedSuggestions.reduce(
      (acc, suggestion) => {
        const targetChef = chefMenuGroups.find(
          (group) => group.id === suggestion.target.id,
        )
        if (targetChef) {
          acc[targetChef.id] = [...(acc[targetChef.id] || []), suggestion]
        }

        return acc
      },
      {} as Record<string, DateMenuSuggestion[]>,
    )
  }, [generatedSuggestions, chefMenuGroups])

  const handleSelectGroupedChefSuggestions = useCallback(
    (chefId: string | null) => {
      setSelectedGroupedChefSuggestions(chefId)
      if (chefId) {
        const chef = chefMenuGroups.find((group) => group.id === chefId)
        if (chef) {
          setMapCenter([chef.coordinates.latitude, chef.coordinates.longitude])
          setSelectedGroupedChefSuggestions(chefId)
        }
      }
    },
    [chefMenuGroups, setMapCenter, setSelectedGroupedChefSuggestions],
  )

  const handleSelectDateMenuSuggestion = useCallback(
    (suggestionId: number | null) => {
      setSelectedSuggestionId(suggestionId)
      if (suggestionId) {
        const suggestion = generatedSuggestions.find(
          (suggestion) => suggestion.id === suggestionId,
        )
        if (suggestion) {
          setMapCenter([
            suggestion.target.coordinates.latitude,
            suggestion.target.coordinates.longitude,
          ])
          setSelectedSuggestionId(suggestionId)
        }
      }
    },
    [generatedSuggestions, setMapCenter, setSelectedSuggestionId],
  )

  const handleSelectSuggestionMenu = useCallback(
    (menuId: string | null) => {
      setSelectedSuggestionMenuId(menuId)
      if (menuId) {
        const menu = menus.find((menu) => menu.id === menuId)
        if (menu) {
          setSelectedSuggestionMenuId(menuId)
        }
      }
    },
    [menus, setSelectedSuggestionMenuId],
  )

  const handleImportCSV = useCallback(
    (data: any[], _fileInfo: any) => {
      if (selectedChefIds.length === 0) {
        displayFailureMessage('No chefs selected')

        return false
      }

      const suggestionsImporter = csvToDateMenuSuggestions(
        displayFailureMessage,
        chefMenuGroups.map((group) => group.chef),
        menus,
        hqId,
      )
      const importedSuggestions = suggestionsImporter(data, _fileInfo)

      if (!importedSuggestions) {
        return false
      }

      // Update state with generated suggestions
      setGeneratedSuggestions(importedSuggestions)
      setHasGeneratedSuggestions(true)
      setSelectedGroupedChefSuggestions(null)
      setSelectedSuggestionId(null)
      setSelectedSuggestionMenuId(null)
      displayInfoMessage(
        `Successfully imported ${importedSuggestions.length} date menu suggestions`,
      )

      return true
    },
    [
      displayFailureMessage,
      displayInfoMessage,
      menus,
      hqId,
      chefMenuGroups,
      selectedChefIds.length,
    ],
  )

  const buildChefMarker = (
    markerId: string,
    chefName: string,
    icon: L.Icon,
    coordinates: Coordinate,
    menus: GOScheduleMenu[],
  ) => {
    return {
      id: markerId,
      position: [coordinates.latitude, coordinates.longitude] as LatLngTuple,
      icon,
      popupContent: (
        <React.Fragment>
          <strong>{chefName}</strong>
          {menus.length > 0 && (
            <React.Fragment>
              <br />
              <strong>Menus:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                {menus.map((menu, menuIndex) => (
                  <li key={menuIndex}>
                    <strong>{menu.name}</strong>
                    {menu.cuisineTags && menu.cuisineTags.length > 0 && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {menu.cuisineTags.join(', ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </React.Fragment>
          )}
        </React.Fragment>
      ),
    }
  }

  // Create markers for chefs and client
  const mapMarkers = useMemo(() => {
    const markers: MapMarker[] = []

    if (selectedSuggestionId !== null && generatedSuggestions.length > 0) {
      const suggestion = generatedSuggestions.find(
        (suggestion) => suggestion.id === selectedSuggestionId,
      )
      if (suggestion) {
        // render target
        markers.push(
          buildChefMarker(
            `selected-suggestion-target-${suggestion.id}`,
            suggestion.target.name,
            purpleChefIcon,
            suggestion.target.coordinates,
            [],
          ),
        )
        // render unselected menus first
        suggestion.menus
          .filter((menu) => menu.id !== selectedSuggestionMenuId)
          .forEach((menu) => {
            markers.push(
              buildChefMarker(
                `selected-suggestion-${suggestion.id}-${menu.id}`,
                menu.chef.name,
                greenChefIcon,
                getMenuCoordinates(menu, hqId),
                [menu],
              ),
            )
          })
        // render selected menu last so its on top
        if (selectedSuggestionMenuId) {
          const selectedMenu = suggestion.menus.find(
            (menu) => menu.id === selectedSuggestionMenuId,
          )
          if (selectedMenu) {
            markers.push(
              buildChefMarker(
                `selected-suggestion-menu-${suggestion.id}-${selectedMenu.id}`,
                selectedMenu.chef.name,
                yellowChefIcon,
                getMenuCoordinates(selectedMenu, hqId),
                [selectedMenu],
              ),
            )
          }
        }
      }
    } else if (selectedGroupedChefSuggestions && groupedChefsSuggestions) {
      const chefSuggestions =
        groupedChefsSuggestions[selectedGroupedChefSuggestions]
      const chef = chefMenuGroups.find(
        (chef) => chef.id === selectedGroupedChefSuggestions,
      )
      if (chefSuggestions && chef) {
        chefSuggestions.forEach((suggestion) => {
          suggestion.route.forEach((index) => {
            if (index < suggestion.menus.length) {
              markers.push(
                buildChefMarker(
                  `selected-chef-suggestion-group-${suggestion.id}-${index}`,
                  suggestion.menus[index].chefName,
                  greenChefIcon,
                  getMenuCoordinates(suggestion.menus[index], hqId),
                  [suggestion.menus[index]],
                ),
              )
            } else {
              markers.push(
                buildChefMarker(
                  `selected-chef-suggestion-group-${suggestion.id}-${index}`,
                  suggestion.target.name,
                  purpleChefIcon,
                  suggestion.target.coordinates,
                  chef.menus,
                ),
              )
            }
          })
        })
      }
    } else {
      const nonSelectedChefs = chefMenuGroups.filter(
        (chefGroup) => !selectedChefIds.includes(chefGroup.id),
      )
      nonSelectedChefs.forEach((chefGroup) => {
        markers.push(
          buildChefMarker(
            `chef-${chefGroup.id}`,
            chefGroup.name,
            purpleChefIcon,
            chefGroup.coordinates,
            chefGroup.menus,
          ),
        )
      })
      // render selected chefs last so they are on top
      const selectedChefs = chefMenuGroups.filter((chefGroup) =>
        selectedChefIds.includes(chefGroup.id),
      )
      selectedChefs.forEach((chefGroup) => {
        markers.push(
          buildChefMarker(
            `selected-chef-${chefGroup.id}`,
            chefGroup.name,
            yellowChefIcon,
            chefGroup.coordinates,
            chefGroup.menus,
          ),
        )
      })
    }

    return markers
  }, [
    chefMenuGroups,
    selectedSuggestionId,
    generatedSuggestions,
    selectedGroupedChefSuggestions,
    groupedChefsSuggestions,
    hqId,
    selectedSuggestionMenuId,
    selectedChefIds,
  ])

  // Create polylines for suggestions
  const mapPolylines = useMemo(() => {
    if (!hasGeneratedSuggestions) {
      return []
    }

    let colors = [
      '#FF5722',
      '#E91E63',
      '#9C27B0',
      '#673AB7',
      '#3F51B5',
      '#2196F3',
      '#009688',
    ]

    const filteredSuggestions = generatedSuggestions.filter((suggestion) => {
      if (selectedSuggestionId !== null) {
        colors = ['#3F51B5']

        return suggestion.id === selectedSuggestionId
      } else if (selectedGroupedChefSuggestions && groupedChefsSuggestions) {
        return groupedChefsSuggestions[selectedGroupedChefSuggestions].some(
          (s) => s.id === suggestion.id,
        )
      }

      return true
    })

    return filteredSuggestions.map((suggestion, suggestionIndex) => {
      // Define different colors for different suggestion routes
      const colorIndex = suggestionIndex % colors.length

      // Create positions array from suggestion route
      const positions: LatLngTuple[] = []

      if (suggestion.route && suggestion.menus.length > 0) {
        // Add positions in the order of the route
        suggestion.route.forEach((index) => {
          if (index < suggestion.menus.length) {
            const menu = suggestion.menus[index]
            const coords = getMenuCoordinates(menu, hqId)
            positions.push([coords.latitude, coords.longitude])
          } else {
            positions.push([
              suggestion.target.coordinates.latitude,
              suggestion.target.coordinates.longitude,
            ] as LatLngTuple)
          }
        })
      }

      return {
        positions,
        pathOptions: {
          color: colors[colorIndex],
          weight: 3,
          opacity: 0.7,
        },
      }
    })
  }, [
    generatedSuggestions,
    hasGeneratedSuggestions,
    hqId,
    selectedSuggestionId,
    selectedGroupedChefSuggestions,
    groupedChefsSuggestions,
  ])

  return {
    chefMenuGroups,
    filteredChefMenuGroups,
    groupedChefsSuggestions,
    mapMarkers,
    mapPolylines,
    mapCenter,
    maxMiles,
    setMaxMiles,
    searchTerm: allChefsSearch,
    setSearchTerm: setAllChefsSearch,
    includesCuisines,
    setIncludesCuisines,
    excludesCuisines,
    setExcludesCuisines,
    selectedChefIds,
    handleSelectChef,
    selectedTemplate,
    selectedSuggestionId,
    selectedGroupedChefSuggestions,
    selectedSuggestionMenuId,
    setSelectedTemplate,
    handleClearSelections,
    handleGenerateSuggestions,
    handleImportCSV,
    hasGeneratedSuggestions,
    generatedSuggestions,
    handleSelectGroupedChefSuggestions,
    handleSelectDateMenuSuggestion,
    handleSelectSuggestionMenu,
    includeAnchorChefs,
    setIncludeAnchorChefs,
  }
}

// Interface for the MarketChefsList component props
interface MarketChefsListProps {
  hqId: number
  userId: string
  userEmail: string
  loadChefs: (params: any) => Promise<Chef[]>
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  searchDateMenuTemplates: (
    req: SearchDateMenuTemplatesReq,
  ) => Promise<DateMenuTemplate[] | null>
  deleteDateMenuTemplates: (id: string) => Promise<boolean>
  allChefMenuGroups: ChefMenuGroup[]
  chefMenuGroups: ChefMenuGroup[]
  menus: GOScheduleMenu[]
  selectedChefIds: string[]
  selectedSuggestionId: number | null
  selectedGroupedChefSuggestions: string | null
  selectedSuggestionMenuId: string | null
  selectedTemplate: DateMenuTemplate | null
  searchTerm: string
  setSearchTerm: (term: string) => void
  includesCuisines: string[]
  setIncludesCuisines: (cuisines: string[]) => void
  excludesCuisines: string[]
  setExcludesCuisines: (cuisines: string[]) => void
  maxMiles: number | null
  setMaxMiles: (miles: number | null) => void
  onSelectChef: (chefId: string) => void
  onSelectTemplate: (template: DateMenuTemplate) => void
  onClearSelections: () => void
  onGenerateSuggestions: () => void
  handleSelectGroupedChefSuggestions: (chefId: string | null) => void
  handleSelectDateMenuSuggestion: (suggestionId: number | null) => void
  handleSelectSuggestionMenu: (menuId: string | null) => void
  handleImportCSV: (data: any[], _fileInfo: any) => boolean
  hasGeneratedSuggestions: boolean
  groupedChefsSuggestions: Record<string, DateMenuSuggestion[]> | null
  generatedSuggestions: DateMenuSuggestion[]
  cuisineOptions: Option[]
  includeAnchorChefs: boolean | null
  setIncludeAnchorChefs: (includeAnchorChefs: boolean | null) => void
  showInfoModal: (req: ShowInfoModalReq) => void
  closeInfoModal: () => void
}

// The MarketChefsList component
export const MarketChefsList: React.FC<MarketChefsListProps> = ({
  hqId,
  userId,
  userEmail,
  loadChefs,
  displayFailureMessage,
  displayInfoMessage,
  displayConfirmationModal,
  chefMenuGroups,
  allChefMenuGroups,
  menus,
  selectedChefIds,
  selectedTemplate,
  searchTerm,
  setSearchTerm,
  includesCuisines,
  setIncludesCuisines,
  excludesCuisines,
  setExcludesCuisines,
  maxMiles,
  setMaxMiles,
  selectedSuggestionId,
  selectedGroupedChefSuggestions,
  selectedSuggestionMenuId,
  generatedSuggestions,
  onSelectChef,
  onSelectTemplate,
  onClearSelections,
  onGenerateSuggestions,
  handleSelectGroupedChefSuggestions,
  handleSelectDateMenuSuggestion,
  handleSelectSuggestionMenu,
  handleImportCSV,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  searchDateMenuTemplates,
  deleteDateMenuTemplates,
  hasGeneratedSuggestions,
  groupedChefsSuggestions,
  cuisineOptions,
  includeAnchorChefs,
  setIncludeAnchorChefs,
  showInfoModal,
  closeInfoModal,
}) => {
  const [expandedGroupedSuggestions, setExpandedGroupedSuggestions] = useState<
    Record<string, boolean>
  >({})
  const [expandedChefs, setExpandedChefs] = useState<Record<string, boolean>>(
    {},
  )
  const [activeTab, setActiveTab] = useState<'market-chefs' | 'date-menus'>(
    hasGeneratedSuggestions ? 'date-menus' : 'market-chefs',
  )

  const csvData = dateMenuSuggestionToCSV(generatedSuggestions)
  const csvFilename = `chefs-date-menu-suggestions-${
    new Date().toISOString().split('T')[0]
  }.csv`

  // Auto-switch to date menus tab when suggestions are generated
  useEffect(() => {
    if (hasGeneratedSuggestions) {
      setActiveTab('date-menus')
    }
  }, [hasGeneratedSuggestions])

  const handleSelectTemplate = (template: DateMenuTemplate) => {
    onSelectTemplate(template)
    closeInfoModal()
  }

  const showTemplatesManager = () => {
    showInfoModal({
      title: `Templates Manager`,
      content: (
        <DateMenuTemplatesManager
          hqId={hqId}
          userId={userId}
          userEmail={userEmail}
          cuisineOptions={cuisineOptions}
          loadChefs={loadChefs}
          displayConfirmationModal={displayConfirmationModal}
          displayInfoMessage={displayInfoMessage}
          displayFailureMessage={displayFailureMessage}
          createDateMenuTemplate={createDateMenuTemplate}
          updateDateMenuTemplate={updateDateMenuTemplate}
          searchDateMenuTemplates={searchDateMenuTemplates}
          deleteDateMenuTemplates={deleteDateMenuTemplates}
          onSelectTemplate={handleSelectTemplate}
        />
      ),
      width: '1650px',
    })
  }

  // Don't render inputs if there are no templates or menus
  if (menus.length === 0) {
    return (
      <React.Fragment>
        <h3>Market Chefs</h3>
        <YSpacing height="10px" />
        <NoResultsMessage>Please find menus first</NoResultsMessage>
      </React.Fragment>
    )
  }

  const renderMarketChefsTab = () => (
    <>
      <p>Select target chefs and a template to generate menu suggestions</p>

      <FiltersSection>
        <FiltersSection>
          <FiltersSectionTitle>Date Menu Template Options</FiltersSectionTitle>
          <Button
            width="100%"
            label={selectedTemplate ? 'Select New Template' : 'Select Template'}
            onClick={showTemplatesManager}
          />
          {selectedTemplate && (
            <>
              <YSpacing height="10px" />
              <InputLabel>Selected template:</InputLabel>
              <SelectedTemplateTitle>
                {selectedTemplate.name} ({selectedTemplate.templates.length}{' '}
                menus)
              </SelectedTemplateTitle>
            </>
          )}
          <YSpacing height="10px" />
          <InputLabel htmlFor="maxMiles">Max Miles from Target:</InputLabel>
          <MaxDistanceInput
            type="number"
            min="1"
            value={maxMiles || ''}
            onChange={(e) => {
              const nextValue = parseFloat(e.target.value)
              setMaxMiles(nextValue ? nextValue : null)
            }}
            placeholder="Enter number"
          />
        </FiltersSection>

        <YSpacing height="10px" />
        <FiltersSection>
          <FiltersSectionTitle>Chef Filters</FiltersSectionTitle>
          <InputLabel htmlFor="chefSearch">
            Search by Chef or Menu Name:
          </InputLabel>
          <SearchInput
            id="chefSearch"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Type to search..."
          />
          <YSpacing height="15px" />

          <InputLabel htmlFor="includeAnchorChefs">Chef Type</InputLabel>
          <Select
            id="includeAnchorChefs"
            value={
              includeAnchorChefs == null
                ? 'all'
                : includeAnchorChefs
                  ? 'anchor'
                  : 'notAnchor'
            }
            onChange={(e) => {
              if (e.target.value === 'all') {
                setIncludeAnchorChefs(null)
              } else {
                setIncludeAnchorChefs(e.target.value === 'anchor')
              }
            }}
          >
            <option value="all">All</option>
            <option value="anchor">Anchor</option>
            <option value="notAnchor">Not Anchor</option>
          </Select>
          <YSpacing height="15px" />

          <InputLabel htmlFor="includesCuisines">
            Must Include at Least One of These Cuisines:
          </InputLabel>
          <MultiSelectDropDown
            label=""
            includeSelectAll={true}
            options={cuisineOptions}
            checkedOptions={includesCuisines}
            searchPlaceholder="Select cuisines..."
            onChange={(values) => setIncludesCuisines(values)}
            width="100%"
            renderCheckedOptions={true}
          />
          <YSpacing height="15px" />

          <InputLabel htmlFor="excludesCuisines">
            Must Not Include Any of These Cuisines:
          </InputLabel>
          <MultiSelectDropDown
            label=""
            includeSelectAll={true}
            options={cuisineOptions}
            checkedOptions={excludesCuisines}
            searchPlaceholder="Select cuisines..."
            onChange={(values) => setExcludesCuisines(values)}
            width="100%"
            renderCheckedOptions={true}
          />

          <YSpacing height="15px" />
          <InputLabel>Selected Chefs: {selectedChefIds.length}</InputLabel>
          {selectedChefIds.map((chefId) => (
            <div
              key={chefId}
              className="flex items-center justify-between p-1 m-1 bg-blue-200 rounded-md"
              onClick={() => onSelectChef(chefId)}
            >
              {allChefMenuGroups.find((group) => group.id === chefId)?.name}
              <FaCircleXmark />
            </div>
          ))}
        </FiltersSection>
        <YSpacing height="10px" />

        <FlexContainer justifyContent="space-between" flexDirection="column">
          <Button
            width="100%"
            label="Clear Selections"
            onClick={onClearSelections}
            disabled={selectedChefIds.length === 0}
          />
          <YSpacing height="10px" />
          <Button
            width="100%"
            label="Generate Suggestions"
            onClick={onGenerateSuggestions}
            disabled={selectedChefIds.length === 0 || !selectedTemplate}
          />
          <ImportContainer>
            <FaFileUpload />
            <CSVReader
              cssClass="csv-import-input"
              cssLabelClass="csv-import-label"
              label="Import from CSV"
              onFileLoaded={(data, _fileInfo) => {
                const success = handleImportCSV(data, _fileInfo)
                if (success) {
                  setActiveTab('date-menus')
                }
              }}
              parserOptions={{
                header: true,
                skipEmptyLines: true,
                transformHeader: (header: string) => header.trim(),
              }}
              inputStyle={{ display: 'none' }}
            />
          </ImportContainer>
          <YSpacing height="10px" />
          <p>* Import via CSV to edit date menus</p>
          <YSpacing height="10px" />
        </FlexContainer>
      </FiltersSection>

      <ResultsDivider />

      <ResultsSection>
        <ResultsSectionTitle>
          Available Chefs ({chefMenuGroups.length})
        </ResultsSectionTitle>

        <FlexContainer flexDirection="column" width="100%">
          {chefMenuGroups.map((chefGroup, index) => (
            <ChefCard
              key={index}
              selected={selectedChefIds.includes(chefGroup.id)}
              onClick={() => onSelectChef(chefGroup.id)}
            >
              <ChefTitle>{chefGroup.name}</ChefTitle>
              <SuggestionDetail>
                <strong>Menus Offered:</strong> {chefGroup.menus.length}
              </SuggestionDetail>
              <MenuListContainer>
                {(expandedChefs[chefGroup.id]
                  ? chefGroup.menus
                  : chefGroup.menus.slice(0, 3)
                ).map((menu, menuIndex) => (
                  <React.Fragment key={menuIndex}>
                    <MenuItemContainer>
                      <MenuItemDetails>
                        <MenuItemName>{menu.name}</MenuItemName>
                        <MenuItemCuisines>
                          {menu.cuisineTags &&
                            menu.cuisineTags.map((cuisine, cuisineIndex) => (
                              <CuisineTag key={cuisineIndex}>
                                {cuisine}
                              </CuisineTag>
                            ))}
                        </MenuItemCuisines>
                      </MenuItemDetails>
                      <ViewDetailsButton
                        onClick={(e) => {
                          e.stopPropagation() // Prevent triggering the parent onClick
                          showInfoModal({
                            title: `${menu.name} Details`,
                            content: <MenuDetails menu={menu} />,
                          })
                        }}
                      >
                        View Details
                      </ViewDetailsButton>
                    </MenuItemContainer>
                    {menuIndex < Math.min(chefGroup.menus.length, 3) - 1 && (
                      <MenuSeparator />
                    )}
                  </React.Fragment>
                ))}
                {chefGroup.menus.length > 3 && (
                  <MoreMenusIndicator>
                    {expandedChefs[chefGroup.id] ? (
                      <div
                        className="flex flex-row items-center underline text-blue-500"
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedChefs((prev) => ({
                            ...prev,
                            [chefGroup.id]: false,
                          }))
                        }}
                      >
                        hide menus
                      </div>
                    ) : (
                      <div
                        className="flex flex-row items-center underline text-blue-500"
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedChefs((prev) => ({
                            ...prev,
                            [chefGroup.id]: true,
                          }))
                        }}
                      >
                        + {chefGroup.menus.length - 3} more menus
                      </div>
                    )}
                  </MoreMenusIndicator>
                )}
              </MenuListContainer>
            </ChefCard>
          ))}
        </FlexContainer>
      </ResultsSection>
    </>
  )

  const renderDateMenusTab = () => (
    <>
      {hasGeneratedSuggestions && groupedChefsSuggestions ? (
        <>
          <p>
            Review generated menu suggestions and select options to view on the
            map
          </p>
          <YSpacing height="15px" />
          {/* CSV Export Button */}
          {generatedSuggestions.length > 0 && (
            <CSVLink
              data={csvData}
              filename={csvFilename}
              style={{
                textDecoration: 'none',
                width: '100%',
                justifyItems: 'center',
              }}
              target="_blank"
            >
              <ExportButton>
                <FaFileDownload />
                Export to CSV
              </ExportButton>
            </CSVLink>
          )}
          <ImportContainer>
            <FaFileUpload />
            <CSVReader
              cssClass="csv-import-input"
              cssLabelClass="csv-import-label"
              label="Import from CSV"
              onFileLoaded={(data, _fileInfo) => {
                const success = handleImportCSV(data, _fileInfo)
                if (success) {
                  setActiveTab('date-menus')
                }
              }}
              parserOptions={{
                header: true,
                skipEmptyLines: true,
                transformHeader: (header: string) => header.trim(),
              }}
              inputStyle={{ display: 'none' }}
            />
          </ImportContainer>
          <YSpacing height="15px" />
          {Object.entries(groupedChefsSuggestions).map(
            ([chefId, suggestions]) => (
              <SuggestionCard
                key={chefId}
                selected={selectedGroupedChefSuggestions === chefId}
              >
                <h4>
                  {chefMenuGroups.find((group) => group.id === chefId)?.name}
                  <input
                    type="button"
                    value="Map"
                    className="bg-blue-200 rounded-md p-1 m-1"
                    onClick={() => {
                      handleSelectGroupedChefSuggestions(chefId)
                      handleSelectDateMenuSuggestion(null)
                    }}
                  />
                </h4>
                <p className="flex flex-row justify-between">
                  <strong>{suggestions.length} suggestions</strong>
                  {expandedGroupedSuggestions[chefId] ? (
                    <FaCaretDown
                      className={`${
                        expandedGroupedSuggestions[chefId]
                          ? 'rotate-180 pointer'
                          : 'pointer'
                      }`}
                      onClick={() =>
                        setExpandedGroupedSuggestions((prev) => ({
                          ...prev,
                          [chefId]: !prev[chefId],
                        }))
                      }
                    />
                  ) : (
                    <FaCaretUp
                      className={`${
                        expandedGroupedSuggestions[chefId]
                          ? 'rotate-180 pointer'
                          : 'pointer'
                      }`}
                      onClick={() =>
                        setExpandedGroupedSuggestions((prev) => ({
                          ...prev,
                          [chefId]: !prev[chefId],
                        }))
                      }
                    />
                  )}
                </p>

                {expandedGroupedSuggestions[chefId] &&
                  suggestions.map((suggestion, index) => (
                    <SuggestionCard
                      key={index}
                      selected={selectedSuggestionId === suggestion.id}
                    >
                      <h4 className="underline text-blue-500 mb-2">
                        <strong>Suggestion {index + 1}</strong>
                        <input
                          type="button"
                          value="Map"
                          className="bg-blue-200 rounded-md p-1 m-1"
                          onClick={() => {
                            handleSelectDateMenuSuggestion(suggestion.id)
                            handleSelectGroupedChefSuggestions(null)
                          }}
                        />
                      </h4>
                      <p>
                        <strong>Distance:</strong>{' '}
                        {suggestion.totalDistance.toFixed(2)} miles
                      </p>
                      <p>
                        <strong>Target Chef:</strong> {suggestion.target.name}
                      </p>
                      <p>
                        <strong>Menus:</strong>
                      </p>
                      <ul>
                        {suggestion.route
                          .map((menuIdx) => suggestion.menus[menuIdx])
                          .filter((menu) => !!menu)
                          .map((menu) => {
                            const underline =
                              menu.id === selectedSuggestionMenuId
                                ? 'underline'
                                : ''
                            const pointer =
                              menu.id === selectedSuggestionMenuId
                                ? 'pointer'
                                : ''

                            return (
                              <li
                                key={menu.id}
                                className={[underline, pointer]
                                  .filter(Boolean)
                                  .join(' ')}
                                onMouseEnter={() => {
                                  if (suggestion.id === selectedSuggestionId) {
                                    handleSelectSuggestionMenu(menu.id)
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (suggestion.id === selectedSuggestionId) {
                                    handleSelectSuggestionMenu(null)
                                  }
                                }}
                              >
                                <strong>{menu.chefName}</strong> - {menu.name} (
                                {menu.cuisineTags?.join(', ')})
                              </li>
                            )
                          })}
                      </ul>
                    </SuggestionCard>
                  ))}
              </SuggestionCard>
            ),
          )}
        </>
      ) : (
        <NoResultsMessage>
          No suggestions generated yet. Please go to the Market Chefs tab to
          select chefs and generate suggestions.
        </NoResultsMessage>
      )}
    </>
  )

  return (
    <React.Fragment>
      <TabContainer>
        <TabHeader>
          <TabButton
            active={activeTab === 'market-chefs'}
            onClick={() => setActiveTab('market-chefs')}
          >
            <FlexContainer
              justifyContent="space-between"
              alignItems="center"
              flexDirection="column"
            >
              Market Chefs
              {selectedChefIds.length > 0 && (
                <TabBadge>{selectedChefIds.length}</TabBadge>
              )}
            </FlexContainer>
          </TabButton>
          <TabButton
            active={activeTab === 'date-menus'}
            onClick={() => setActiveTab('date-menus')}
            disabled={!hasGeneratedSuggestions}
          >
            <FlexContainer
              justifyContent="space-between"
              alignItems="center"
              flexDirection="column"
            >
              Date Menus
              {generatedSuggestions.length > 0 && (
                <TabBadge>{generatedSuggestions.length}</TabBadge>
              )}
            </FlexContainer>
          </TabButton>
        </TabHeader>

        <TabContent>
          {activeTab === 'market-chefs'
            ? renderMarketChefsTab()
            : renderDateMenusTab()}
        </TabContent>
      </TabContainer>
    </React.Fragment>
  )
}

// Styled components
const BaseCard = styled.div`
  padding: 15px;
  margin-bottom: 12px;
  background-color: white;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
`

const ChefCard = styled(BaseCard)<{ selected: boolean }>`
  cursor: pointer;
  background-color: ${(props) => (props.selected ? colors.gray200 : 'white')};
  border: 1px solid ${colors.gray200};

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    background-color: ${(props) =>
      props.selected ? colors.gray200 : colors.gray100};
  }
`

const SuggestionCard = styled(BaseCard)<{ selected: boolean }>`
  margin-bottom: 10px;
  border: 1px solid ${colors.blue200};
  background-color: ${(props) => (props.selected ? colors.gray200 : 'white')};

  h4 {
    margin-top: 0;
    color: ${colors.blue400};
  }

  ul {
    margin-top: 5px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 5px;
  }
`

const ChefTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${colors.blue400};
`

const SuggestionDetail = styled.p`
  margin: 4px 0;
  font-size: 0.9em;
  color: ${colors.gray400};
  line-height: 1.3;

  strong {
    color: ${colors.dark500};
    font-weight: 500;
  }
`

const MenuListContainer = styled.div`
  width: 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${colors.gray200};
`

const MenuItemContainer = styled.div`
  margin: 10px 0;
  padding: 8px;
  background-color: ${colors.gray100};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const MenuItemDetails = styled.div`
  flex: 1;
`

const MenuItemName = styled.div`
  font-size: 0.95em;
  font-weight: 500;
  color: ${colors.dark500};
  margin-bottom: 3px;
`

const MenuItemCuisines = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const MenuSeparator = styled.div`
  height: 1px;
  background-color: ${colors.gray100};
  margin: 10px 0;
`

const MoreMenusIndicator = styled.div`
  text-align: center;
  font-size: 0.85em;
  color: ${colors.gray400};
  font-style: italic;
  padding: 5px 0;
`

const InputLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${colors.dark500};
`

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  font-size: 0.95em;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.blue400};
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue100};
  }
`

const NoResultsMessage = styled.p`
  text-align: center;
  color: ${colors.gray400};
  font-style: italic;
`

const FiltersSection = styled.div`
  background-color: ${colors.gray100};
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${colors.gray200};
`

const FiltersSectionTitle = styled.h4`
  color: ${colors.blue400};
  margin: 0 0 15px 0;
  font-size: 1.05em;
  font-weight: 600;
  border-bottom: 1px solid ${colors.gray200};
  padding-bottom: 8px;
`

const ResultsDivider = styled(DividerLine)`
  margin: 0 0 20px 0;
`

const ResultsSection = styled.div`
  margin-top: 15px;
`

const ResultsSectionTitle = styled.h4`
  color: ${colors.blue400};
  margin: 0 0 15px 0;
  font-size: 1.05em;
  font-weight: 600;
`

const CuisineTag = styled.span`
  background-color: ${colors.blue100};
  border: 1px solid ${colors.blue200};
  color: ${colors.blue400};
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.7em;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  font-size: 0.95em;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.blue400};
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue100};
  }
`

const ViewDetailsButton = styled.button`
  background-color: ${colors.blue300};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.blue400};
  }
`

const MaxDistanceInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  font-size: 14px;
`

// New styled components for tabs
const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.gray200};
  margin-bottom: 20px;
`

const TabButton = styled.button<{ active: boolean; disabled?: boolean }>`
  padding: 10px 20px;
  background: ${(props) => (props.active ? colors.blue100 : 'transparent')};
  color: ${(props) =>
    props.active
      ? colors.blue400
      : props.disabled
        ? colors.gray400
        : colors.dark500};
  border: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? colors.blue400 : 'transparent')};
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  &:hover {
    background: ${(props) => (props.disabled ? 'transparent' : colors.gray100)};
  }
`

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue400};
  color: white;
  border-radius: 999px;
  font-size: 0.75rem;
  padding: 2px 6px;
  min-width: 18px;
  height: 18px;
`

const TabContent = styled.div`
  padding: 0;
`

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95em;
  transition: background-color 0.2s ease;
  width: 100%;
  text-align: center;

  svg {
    margin-right: 8px;
  }

  &:hover {
    background-color: ${colors.green300};
  }
`

const ImportContainer = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;

  svg {
    position: absolute;
    left: 12px;
    color: white;
    z-index: 1;
  }

  .csv-import-input {
    width: 100%;
  }

  .csv-import-label {
    background-color: ${colors.blue};
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 12px 8px 35px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.95em;
    display: flex;
    justify-content: center;
    width: 100%;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${colors.blue300};
    }
  }
`

const SelectedTemplateTitle = styled.p`
  font-size: 1.1em;
  color: ${colors.blue};
  font-style: italic;
  text-decoration: underline;
  padding-left: 5px;
`
